import {
  DndContext,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import isEqual from 'react-fast-compare';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import {
  Content,
  getContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { useReportMetricsContextV2 } from 'src/contexts/ReportMetricsContext/ReportMetricsContextV2';
import { ConfirmDialog } from 'src/core/interim/dialogs/ConfirmDialog';
import { PosFormField } from 'src/core/POS/PosFormField';
import { shared } from 'src/core/themes';
import { Stack, Switch } from 'src/core/ui';
import { ReportConfigV2 } from 'src/hooks/reportConfigs';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { TableCustomColumnModalReporting } from 'src/modals/ColumnSettings/TableCustomColumnModal/TableCustomColumnModalReporting';
import { ReportColumnPersonalizationFlags } from 'src/utils/columns/columnUtils.types';
import {
  getCustomColumnDisplayName,
  isCustomColumnName,
} from 'src/utils/columns/reportV2CustomColumnUtils';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getTagDisplayNameFromReportingColumnName,
  isTagReportingColumnName,
  ReportTypesV2,
  reportValueColumnNameToExpression,
} from 'src/utils/reportsUtils';
import { hasFeatures } from 'src/utils/userUtils';
import {
  Feature,
  FeatureDetail,
  ReportColumn,
  UserDetail,
} from 'src/WebApiController';

import { MetricsInputDroppable } from './MetricsInputDroppable';
import { MetricsOptionsList } from './MetricsOptionsList';
import { MetricsOptionsListItem } from './MetricsOptionsListItem';
import * as styles from './ReportsTableEditor.css';
import {
  REPORT_TYPE_TO_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES,
  REPORT_TYPE_TO_GROUPING_CONTENT_OPTIONS,
  REPORT_TYPE_TO_VALUE_CONTENT_OPTIONS,
} from './ReportsTableEditorForm.constants';
import { useReportsCustomColumn } from './useReportsCustomColumn';
import { useReportsTags } from './useReportsTags';

const filterByFeature = (
  options: Record<string, ContentId>,
  personalizationFlags: Record<
    string,
    Partial<ReportColumnPersonalizationFlags>
  >,
  user: UserDetail | null | undefined,
  features: FeatureDetail[] | undefined,
  excludeSellerAccountCurrencyColumns = false
) => {
  return Object.entries(options).reduce(
    (acc, [key, value]) => {
      // Skip SellerAccountCurrency columns if requested
      if (
        excludeSellerAccountCurrencyColumns &&
        key.endsWith('SellerAccountCurrency')
      ) {
        return acc;
      }

      let hasNeededFeatures = true;

      const personalization = personalizationFlags[key];
      if (personalization?.requiredFeatures?.length) {
        if (!hasFeatures(user, features, personalization.requiredFeatures)) {
          hasNeededFeatures = false;
        }
      }

      if (hasNeededFeatures) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, ContentId>
  );
};

export const ReportsTableEditorForm = ({
  reportConfig,
  disabled,
}: {
  reportConfig: ReportConfigV2;
  disabled?: boolean;
}) => {
  const methods = useForm<ReportConfigV2>({
    defaultValues: reportConfig,
  });

  // Report config can be changed elsewhere like the filter bar
  useEffect(() => {
    if (!isEqual(methods.formState.defaultValues, reportConfig)) {
      methods.reset(reportConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportConfig]);

  return (
    <FormProvider {...methods}>
      <ReportsTableEditorContent {...methods} disabled={disabled} />
    </FormProvider>
  );
};

const ROWS_INPUT_KEY = 'rows-input';
const VALUES_INPUT_KEY = 'values-input';
const ROWS_OPTIONS_LIST_KEY = 'rows-options-list';
const VALUES_OPTIONS_LIST_KEY = 'values-options-list';

const SELLER_ACCOUNT_CURRENCY_COLUMN_MAPPING: Record<string, string> = {
  totalCost: 'totalCostSellerAccountCurrency',
  totalUnitNetProceeds: 'totalNetProceedsSellerAccountCurrency',
  totalNetProceeds: 'totalNetProceedsSellerAccountCurrency',
  outstandingBalance: 'outstandingBalanceSellerAccountCurrency',
  charges: 'chargesSellerAccountCurrency',
  credits: 'creditsSellerAccountCurrency',
  PandL: 'pandLSellerAccountCurrency',
  cost: 'costSellerAccountCurrency',
  charge: 'chargeSellerAccountCurrency',
  credit: 'creditSellerAccountCurrency',
  totalPaymentAmount: 'totalPaymentAmountSellerAccountCurrency',
};

const ReportsTableEditorContent = ({
  watch,
  formState,
  handleSubmit,
  setValue,
  disabled,
}: Omit<
  ComponentProps<typeof FormProvider<ReportConfigV2, unknown>>,
  'children' | 'trigger' | 'reset' | 'unregister'
> & {
  disabled?: boolean;
}) => {
  const { appContext, loginContext } = useAppContext();
  const metricsError = formState.errors.request?.aggregations?.message;
  const contentContext = useContentContext();

  const groupbyField = 'request.rowGroupings';
  const sortByField = 'request.orderBy';
  const metricsField = 'request.aggregations';

  const reportType = watch('reportType');
  const groupBy = watch(groupbyField);
  const metrics = watch(metricsField);
  const sortBy = watch(sortByField);
  const requiredRowGroupingNamesValue = watch('requiredRowGroupingNames');

  const requiredRowGroupingNames = useMemo(() => {
    return requiredRowGroupingNamesValue || [];
  }, [requiredRowGroupingNamesValue]);

  const { updateTempReportConfig } = useReportMetricsContextV2();

  const hasAlignCurrenciesOnReportsFeature = useUserHasFeature(
    Feature.AlignCurrenciesOnReports
  );

  // State for currency toggle - only for Sale and Sale Line Item reports
  const [useAccountCurrency, setUseAccountCurrency] = useState(false);

  // Show toggle only for Sale and Sale Line Item reports with the feature flag
  const showCurrencyToggle = useMemo(() => {
    return (
      hasAlignCurrenciesOnReportsFeature &&
      (reportType === ReportTypesV2.SaleV2 ||
        reportType === ReportTypesV2.SaleLineItem)
    );
  }, [hasAlignCurrenciesOnReportsFeature, reportType]);

  const {
    isCustomColumnModalOpen,
    toggleCustomColumnModal,
    customColumnToEdit,
    customColumnNameToDelete,
    setCustomColumnNameToDelete,
    onEditCustomColumn,
    onDeleteCustomColumn,
    onCreateCustomColumn,
    onUpsertCustomColumnSubmit,
    onDeleteCustomColumnSubmit,
    deleteCustomColumnConfirmDialog,
    customReportColumns,
  } = useReportsCustomColumn({
    reportType,
  });

  const { reportingTagNames } = useReportsTags({
    reportType,
  });

  const onSubmit = useCallback(
    async (report: ReportConfigV2) => {
      if (disabled) {
        return;
      }

      // Add useAccountCurrency to the report request for Sale and SaleLineItem reports
      const reportToSubmit = { ...report };

      if (showCurrencyToggle) {
        reportToSubmit.request = {
          ...reportToSubmit.request,
          useAccountCurrency: useAccountCurrency,
        };
      }

      if (
        !isEqual(
          reportToSubmit.request.rowGroupings,
          formState.defaultValues?.request?.rowGroupings
        ) ||
        !isEqual(
          reportToSubmit.request.orderBy,
          formState.defaultValues?.request?.orderBy
        ) ||
        !isEqual(
          reportToSubmit.request.columnGroupings,
          formState.defaultValues?.request?.columnGroupings
        ) ||
        !isEqual(
          reportToSubmit.request.aggregations,
          formState.defaultValues?.request?.aggregations
        ) ||
        (showCurrencyToggle &&
          reportToSubmit.request.useAccountCurrency !==
            formState.defaultValues?.request?.useAccountCurrency)
      ) {
        await updateTempReportConfig(reportToSubmit);
      }
    },
    [
      disabled,
      formState.defaultValues?.request?.aggregations,
      formState.defaultValues?.request?.columnGroupings,
      formState.defaultValues?.request?.orderBy,
      formState.defaultValues?.request?.rowGroupings,
      formState.defaultValues?.request?.useAccountCurrency,
      showCurrencyToggle,
      updateTempReportConfig,
      useAccountCurrency,
    ]
  );

  useEffect(() => {
    handleSubmit(onSubmit)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, metrics, useAccountCurrency]);

  // Recalculate columns when currency toggle changes
  useEffect(() => {
    if (showCurrencyToggle && items[VALUES_INPUT_KEY].length > 0) {
      recalculateReportColumns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAccountCurrency]);

  const existingCustomColumnNames = useMemo(
    () => customReportColumns.map((c) => c.columnName),
    [customReportColumns]
  );

  const groupingOptions = useMemo(() => {
    return filterByFeature(
      REPORT_TYPE_TO_GROUPING_CONTENT_OPTIONS[reportType],
      REPORT_TYPE_TO_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES[reportType],
      loginContext?.user,
      appContext?.features
    );
  }, [appContext?.features, loginContext?.user, reportType]);

  const valueOptions = useMemo(() => {
    return filterByFeature(
      REPORT_TYPE_TO_VALUE_CONTENT_OPTIONS[reportType],
      REPORT_TYPE_TO_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES[reportType],
      loginContext?.user,
      appContext?.features,
      showCurrencyToggle // Exclude SellerAccountCurrency columns when toggle is available
    );
  }, [
    appContext?.features,
    loginContext?.user,
    reportType,
    showCurrencyToggle,
  ]);

  const allGroupingOptions = useMemo(() => {
    const groupingOptionsSortedByContent = Object.entries(groupingOptions)
      .toSorted(([, a], [, b]) =>
        getContent(a, contentContext).localeCompare(
          getContent(b, contentContext)
        )
      )
      .map(([key, value]) => key);

    const reportingTagsSortedByDisplayName = reportingTagNames.toSorted(
      (a, b) =>
        getTagDisplayNameFromReportingColumnName(a).localeCompare(
          getTagDisplayNameFromReportingColumnName(b)
        )
    );

    return [
      ...groupingOptionsSortedByContent,
      ...reportingTagsSortedByDisplayName,
    ];
  }, [contentContext, groupingOptions, reportingTagNames]);

  const allAggregationOptions = useMemo(() => {
    const valueOptionsSortedByContent = Object.entries(valueOptions)
      .toSorted(([, a], [, b]) =>
        getContent(a, contentContext).localeCompare(
          getContent(b, contentContext)
        )
      )
      .map(([key, value]) => key);

    const customColumnsSortedByDisplayName = customReportColumns
      .toSorted((a, b) =>
        getCustomColumnDisplayName(a.columnName).localeCompare(
          getCustomColumnDisplayName(b.columnName)
        )
      )
      .map((c) => c.columnName);

    return [
      ...valueOptionsSortedByContent,
      ...customColumnsSortedByDisplayName,
    ];
  }, [contentContext, valueOptions, customReportColumns]);

  const [items, setItems] = useState<Record<string, string[]>>({
    [ROWS_INPUT_KEY]: [],
    [VALUES_INPUT_KEY]: [],
    [ROWS_OPTIONS_LIST_KEY]: [],
    [VALUES_OPTIONS_LIST_KEY]: [],
  });

  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const groupByItems = (groupBy || []).map((c: ReportColumn) => c.columnName);
    const metricsItems = (metrics || []).map((c: ReportColumn) => c.columnName);
    const availableGroupByOptionsItems = allGroupingOptions.filter(
      (columnName) => !groupByItems.some((g: string) => g === columnName)
    );
    const availableAggregationOptionsItems = allAggregationOptions.filter(
      (columnName) => !metricsItems.some((m: string) => m === columnName)
    );

    let hasChanged = false;
    if (!isEqual(items[ROWS_INPUT_KEY], groupByItems)) {
      hasChanged = true;
    }
    if (!isEqual(items[VALUES_INPUT_KEY], metricsItems)) {
      hasChanged = true;
    }
    if (!isEqual(items[ROWS_OPTIONS_LIST_KEY], availableGroupByOptionsItems)) {
      hasChanged = true;
    }
    if (
      !isEqual(items[VALUES_OPTIONS_LIST_KEY], availableAggregationOptionsItems)
    ) {
      hasChanged = true;
    }
    if (hasChanged) {
      setItems({
        [ROWS_INPUT_KEY]: groupByItems,
        [VALUES_INPUT_KEY]: metricsItems,
        [ROWS_OPTIONS_LIST_KEY]: availableGroupByOptionsItems,
        [VALUES_OPTIONS_LIST_KEY]: availableAggregationOptionsItems,
      });
    }
    // IMPORTANT: Don't add `items` as a dependency here, it will cause an infinite loop
  }, [metrics, groupBy, allGroupingOptions, allAggregationOptions]);

  const findContainerKey = useCallback(
    (overId: string) => {
      if (items[ROWS_INPUT_KEY].includes(overId) || overId === ROWS_INPUT_KEY) {
        return ROWS_INPUT_KEY;
      }
      if (
        items[VALUES_INPUT_KEY].includes(overId) ||
        overId === VALUES_INPUT_KEY
      ) {
        return VALUES_INPUT_KEY;
      }
      if (
        items[ROWS_OPTIONS_LIST_KEY].includes(overId) ||
        overId === ROWS_OPTIONS_LIST_KEY
      ) {
        return ROWS_OPTIONS_LIST_KEY;
      }
      if (
        items[VALUES_OPTIONS_LIST_KEY].includes(overId) ||
        overId === VALUES_OPTIONS_LIST_KEY
      ) {
        return VALUES_OPTIONS_LIST_KEY;
      }
      return null;
    },
    [items]
  );

  const moveOver = (activeId: string, overId: string) => {
    const overContainerKey = findContainerKey(overId);
    if (!overContainerKey) {
      return;
    }
    const activeContainerKey = findContainerKey(activeId);
    if (!activeContainerKey) {
      return;
    }

    // Stop-gap to prevent moving grouping options to values and vice versa
    if (activeContainerKey !== overContainerKey) {
      if (
        overContainerKey === ROWS_INPUT_KEY &&
        !Object.keys(groupingOptions).includes(activeId) &&
        !reportingTagNames.includes(activeId)
      ) {
        return; // Prevent moving non-grouping options to rows
      }

      if (
        overContainerKey === VALUES_INPUT_KEY &&
        !Object.keys(valueOptions).includes(activeId) &&
        !existingCustomColumnNames.includes(activeId)
      ) {
        return; // Prevent moving non-value options to values
      }

      if (
        overContainerKey === ROWS_OPTIONS_LIST_KEY &&
        (allAggregationOptions.includes(activeId) ||
          requiredRowGroupingNames.includes(activeId))
      ) {
        return; // Prevent moving values or required groupings to grouping options
      }
      if (
        overContainerKey === VALUES_OPTIONS_LIST_KEY &&
        allGroupingOptions.includes(activeId)
      ) {
        return; // Prevent moving grouping options to values
      }
    }

    const newItems = { ...items };
    const activeIndex = newItems[activeContainerKey].indexOf(activeId);
    const activeItems = newItems[activeContainerKey].filter(
      (item) => item !== activeId
    );
    newItems[activeContainerKey] = activeItems;

    let overIndex = newItems[overContainerKey].indexOf(overId);
    if (overIndex === -1) {
      // If overId is the container itself, set it to the end of the list
      overIndex = newItems[overContainerKey].length;
    }

    // Move down the overIndex if the active item is before it in the same container
    if (activeIndex <= overIndex && activeContainerKey === overContainerKey) {
      overIndex++;
    }

    const overItems = [
      ...newItems[overContainerKey].slice(0, overIndex),
      activeId,
      ...newItems[overContainerKey].slice(overIndex),
    ];
    newItems[overContainerKey] = overItems;

    setItems(newItems);
  };

  const recalculateReportColumns = useCallback(() => {
    const groupByWithRequiredNames = items[ROWS_INPUT_KEY];
    if (requiredRowGroupingNames.length > 0) {
      groupByWithRequiredNames.push(
        ...requiredRowGroupingNames.filter(
          (name) => !groupByWithRequiredNames.includes(name)
        )
      );
    }

    const newGroupBy = groupByWithRequiredNames.map((columnName) => ({
      columnName,
      columnValue: columnName, // Assume columnValue is the same as columnName
    }));

    const newSortBy = groupByWithRequiredNames[0];
    if (sortBy !== newSortBy) {
      setValue(sortByField, newSortBy);
    }

    if (!isEqual(newGroupBy, groupBy)) {
      setValue(groupbyField, newGroupBy);
    }

    const newMetrics = items[VALUES_INPUT_KEY].filter((columnName) => {
      return isCustomColumnName(columnName)
        ? customReportColumns.find(
            (c) => c.columnName.toLowerCase() === columnName.toLowerCase()
          ) != null
        : true;
    }).map((columnName) => {
      // For custom columns, use the stored value
      if (isCustomColumnName(columnName)) {
        return {
          columnName,
          columnValue: customReportColumns.find(
            (c) => c.columnName.toLowerCase() === columnName.toLowerCase()
          )!.columnValue,
        };
      }

      // For standard columns, check if we should use seller account currency variant
      let columnToAggregate = columnName;
      if (useAccountCurrency && showCurrencyToggle) {
        // Check if this column has a seller account currency variant
        const sellerCurrencyColumn =
          SELLER_ACCOUNT_CURRENCY_COLUMN_MAPPING[columnName];
        if (sellerCurrencyColumn) {
          columnToAggregate = sellerCurrencyColumn;
        }
      }

      return {
        columnName, // Keep the original column name for the UI
        columnValue: reportValueColumnNameToExpression(columnToAggregate),
      };
    });

    if (!isEqual(newMetrics, metrics)) {
      setValue(metricsField, newMetrics);
    }
  }, [
    items,
    requiredRowGroupingNames,
    sortBy,
    groupBy,
    metrics,
    setValue,
    customReportColumns,
    useAccountCurrency,
    showCurrencyToggle,
  ]);

  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!active || !over) {
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;

    // Prevent dragging over itself
    if (activeId === overId) {
      return;
    }

    moveOver(activeId, overId);
  };

  const onDragEnd = (event: DragOverEvent) => {
    recalculateReportColumns();
    setActiveId(null);
  };

  const onDragStart = (event: DragStartEvent) => {
    const { active } = event;
    if (!active) {
      return;
    }

    const activeId = active.id as string;
    setActiveId(activeId);
  };

  return (
    <>
      <DndContext
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        onDragStart={onDragStart}
      >
        <Stack direction="row" gap="s" style={{ overflowY: 'auto' }}>
          <div className={styles.pivotGridContainer}>
            <Stack className={styles.pivotGridItem} direction="column">
              <PosFormField
                errors={metricsError}
                label={
                  <span className={shared.typography.body2}>
                    <Content id={ContentId.RowsGroupBy} />
                  </span>
                }
              />
              <div className={styles.pivotGridItemInner}>
                <MetricsInputDroppable
                  id={ROWS_INPUT_KEY}
                  requiredColumnNames={requiredRowGroupingNames}
                  valueOptionsContent={groupingOptions}
                  selectedValues={items[ROWS_INPUT_KEY]}
                  reportType={reportType}
                />
              </div>
            </Stack>
            <Stack className={styles.pivotGridItem} direction="column">
              <PosFormField
                label={
                  <span className={shared.typography.body2}>
                    <Content id={ContentId.Values} />
                  </span>
                }
              />
              <div className={styles.pivotGridItemInner}>
                <MetricsInputDroppable
                  id={VALUES_INPUT_KEY}
                  valueOptionsContent={valueOptions}
                  selectedValues={items[VALUES_INPUT_KEY]}
                  reportType={reportType}
                />
              </div>
            </Stack>
          </div>
          <div className={styles.pivotGridContainerNoGap}>
            <MetricsOptionsList
              titleContentId={ContentId.RowAttributes}
              id={ROWS_OPTIONS_LIST_KEY}
              availableValues={items[ROWS_OPTIONS_LIST_KEY]}
              optionsContent={groupingOptions}
              disabled={disabled}
              reportType={reportType}
            />
            <MetricsOptionsList
              titleContentId={ContentId.ValueAttributes}
              isValueOptions
              id={VALUES_OPTIONS_LIST_KEY}
              availableValues={items[VALUES_OPTIONS_LIST_KEY]}
              optionsContent={valueOptions}
              disabled={disabled}
              onEditCustomColumn={onEditCustomColumn}
              onDeleteCustomColumn={onDeleteCustomColumn}
              onCreateCustomColumn={onCreateCustomColumn}
              reportType={reportType}
              customHeaderContent={
                showCurrencyToggle ? (
                  <Stack
                    direction="row"
                    gap="s"
                    style={{
                      alignItems: 'center',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                    }}
                  >
                    <Switch
                      checked={useAccountCurrency}
                      onCheckedChange={setUseAccountCurrency}
                      disabled={disabled}
                    />
                    <span className={shared.typography.body2}>
                      <Content id={ContentId.UseAccountCurrency} />
                    </span>
                  </Stack>
                ) : undefined
              }
            />
          </div>
        </Stack>
        <DragOverlay>
          {activeId ? (
            <MetricsOptionsListItem
              key={`active-${activeId}`}
              id={activeId}
              disabled={disabled}
              isDragging={true}
            >
              <Content
                id={
                  isCustomColumnName(activeId)
                    ? getCustomColumnDisplayName(activeId)
                    : isTagReportingColumnName(activeId)
                      ? getTagDisplayNameFromReportingColumnName(activeId)
                      : (valueOptions as Record<string, ContentId>)[activeId] ||
                        (groupingOptions as Record<string, ContentId>)[activeId]
                }
              />
            </MetricsOptionsListItem>
          ) : null}
        </DragOverlay>
      </DndContext>
      {isCustomColumnModalOpen && (
        <TableCustomColumnModalReporting
          onClose={toggleCustomColumnModal}
          columnName={customColumnToEdit?.columnName}
          columnValue={customColumnToEdit?.columnValue}
          onSave={onUpsertCustomColumnSubmit}
          reportType={reportType}
          existingCustomColumns={customReportColumns}
        />
      )}

      <ConfirmDialog
        {...deleteCustomColumnConfirmDialog.dialogProps}
        headerText={<Content id={ContentId.DeleteCustomColumn} />}
        bodyText={<Content id={ContentId.AreYouSure} />}
        onOkay={() => {
          if (customColumnNameToDelete) {
            onDeleteCustomColumnSubmit(customColumnNameToDelete);
            setCustomColumnNameToDelete(undefined);
          }

          deleteCustomColumnConfirmDialog.closeDialog();
        }}
        onCancel={() => deleteCustomColumnConfirmDialog.closeDialog()}
      />
    </>
  );
};
